import omit from 'lodash/omit';
import type { DocNode } from '@atlaskit/adf-schema';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { convertTextToAdf } from '@atlassian/jira-issue-field-textarea/src/ui/edit/utils';
import type { DescriptionAndParagraphPublicValue } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/fields/paragraph';
import type { Field } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/types/field';
import type { FieldInternalMetadata } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/types/field-internal-metadata';
import { isValidADF } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/utils/is-valid-adf';
import type { ViewType } from '@atlassian/jira-ui-modifications-types';

type DocNodeWithChangeFlag = DocNode & {
	overrideRequired: boolean;
};
export type DescriptionAndParagraphInternalValue = string | DocNodeWithChangeFlag | undefined;

/**
 * Below field types are duplicates from `@atlassian/jira-issue-create`
 * located in src/packages/issue-create/issue-create-modal/src/common/constants/graphql-field-types.tsx
 * This was done to prevent cyclic dependencies between packages.
 * Only field types the UIM currently support or plan to support were taken from the referenced file.
 */
export const TEXT_AREA_FIELD = 'JiraTextAreaField';
export const RICH_TEXT_FIELD = 'JiraRichTextField';

export const validateDescriptionAndParagraphSetValue = (
	value: unknown,
	{ fieldRenderer }: FieldInternalMetadata,
	_createAnalyticsEvent: CreateUIAnalyticsEvent,
	extra: { currentField: Field; viewType: ViewType },
): Promise<boolean> => {
	switch (fieldRenderer) {
		case RICH_TEXT_FIELD:
			return isValidADF({
				newValue: value,
				currentValue: extra.currentField?.value,
				viewType: extra.viewType,
			});
		case TEXT_AREA_FIELD:
			return Promise.resolve(typeof value === 'string');
		default:
			return Promise.resolve(false);
	}
};

// We only want to pass expected properties through to setFieldValue
export const descriptionAndParagraphSetValueTransformer = (
	value: DescriptionAndParagraphPublicValue,
): DescriptionAndParagraphInternalValue => {
	if (typeof value === 'string') {
		return value;
	}
	return { ...value, overrideRequired: true };
};

export const descriptionAndParagraphGetValueTransformer = (
	value: DescriptionAndParagraphInternalValue,
	fieldRenderer?: string,
): DescriptionAndParagraphPublicValue => {
	if (value === undefined) {
		// The `convertTextToAdf` call will give us an "empty ADF object":
		// {
		//   "type": "doc",
		//   "version": 1,
		//   "content": [
		//     {
		//       "type": "paragraph",
		//       "content": []
		//     }
		//   ]
		// }
		return fieldRenderer === RICH_TEXT_FIELD ? convertTextToAdf('') : '';
	}
	if (typeof value === 'string') {
		return value;
	}
	return omit(value, 'overrideRequired');
};
